import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { api } from "../lib/api";
import { formatMoney } from "../lib/format";
import { useT } from "../i18n";

export interface ReceiptTarget {
  bookingId: string;
  balanceMinor: number;
  depositDueMinor: number;
  paidMinor: number;
}

/**
 * Cash/bank receipt entry — the hall desk's daily bread (docs/01 §1).
 * Records as pending-verification; the owner verifies it on the finance page
 * (maker–checker), which posts it to the ledger and confirms the booking.
 */
export function ReceiptDialog({
  orgId,
  target,
  onClose,
  onRecorded,
}: {
  orgId: string;
  target: ReceiptTarget | null;
  onClose: () => void;
  onRecorded: (result: { receiptNo: string; paymentId: string }) => void;
}) {
  const suggested = target
    ? Math.max(Math.min(target.depositDueMinor - target.paidMinor, target.balanceMinor), 0) ||
      target.balanceMinor
    : 0;
  const [amountAfn, setAmountAfn] = useState<string>("");
  const [method, setMethod] = useState<"cash" | "bank">("cash");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = useT();

  if (!target) return null;
  const amountMinor = Math.round(Number(amountAfn || 0) * 100);

  async function submit() {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ paymentId: string; receiptNo: string }>(
        `/orgs/${orgId}/payments/manual`,
        {
          method: "POST",
          idempotent: true,
          body: {
            bookingId: target.bookingId,
            method,
            amountMinor,
            currency: "AFN",
            note: note || undefined,
          },
        },
      );
      onRecorded({ receiptNo: result.receiptNo, paymentId: result.paymentId });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("receipt_failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("receipt_title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Typography variant="body2" color="text.secondary">
            {t("receipt_balance", { amount: formatMoney(target.balanceMinor) })}
          </Typography>
          <TextField
            label={t("receipt_amount_label")}
            type="number"
            value={amountAfn}
            onChange={(e) => setAmountAfn(e.target.value)}
            placeholder={String(Math.round(suggested / 100))}
            fullWidth
            autoFocus
          />
          <TextField
            select
            label={t("receipt_method_label")}
            value={method}
            onChange={(e) => setMethod(e.target.value as "cash" | "bank")}
            fullWidth
          >
            <MenuItem value="cash">{t("receipt_method_cash")}</MenuItem>
            <MenuItem value="bank">{t("receipt_method_bank")}</MenuItem>
          </TextField>
          <TextField
            label={t("receipt_note_label")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common_cancel")}</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || amountMinor <= 0 || amountMinor > target.balanceMinor}
        >
          {t("receipt_submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
